import { Button } from "@/components/ui/button"
import { DrawerShell } from "./drawer-shell"
import {
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Plus,
} from "lucide-react"
import type {
  Placement,
  PlacementFormState,
  ContactStatus,
  ProfileType,
  Lob,
} from "./types"
import { MappingSection } from "./sections/mapping-section"
import { WorkerSetupSection } from "./sections/worker-setup-section"
import { DatesRatesSection } from "./sections/dates-rates-section"
import { TemplateSection } from "./sections/template-section"
import type { SubmitResult } from "@/api/onboarding-api.types"
import type { Result } from "@/types/result"
import type { SearchableSelectOption } from "./fields/searchable-select"
import type { CurrencyItem } from "./fields/numeric-field"
import { usePlacementForm } from "@/hooks/use-placement-form"
import { useTranslation } from "@/hooks/use-translation"
import { env } from "@/lib/env"

/** One entry per distinct contact for the worker dropdown */
export interface ContactOption extends SearchableSelectOption {
  contactId: string
  profileTypeCodes: string[]
  email: string
  contactStatus: ContactStatus
}

export interface PlacementDetailsContentProps {
  placement: Placement
  profileTypes: ProfileType[]
  lobs: Lob[]
  currencies: CurrencyItem[]
  /** Full human-readable id shown in the drawer header, e.g. "ATS-12345" or "CR-42". */
  idDisplayText: string
  recordNoun: string
  title: string
  sourceName: string
  onClose: () => void
  onSubmit: (form: PlacementFormState) => Promise<Result<SubmitResult>>
}

export function PlacementDetailsContent(props: PlacementDetailsContentProps) {
  const { t } = useTranslation()
  const { placement, profileTypes, lobs, currencies, idDisplayText, recordNoun, title, sourceName, onClose } = props
  // Short label used in "(from ATS)" tooltips and mismatch copy — derived from the display text's prefix.
  const sourceLabel = idDisplayText.split("-")[0] || idDisplayText

  const {
    form,
    actions,
    validation,
    submission,
    worker,
    workerOrg,
    search,
    templates,
    creationItems,
    diffs,
  } = usePlacementForm({ ...props, fboAppUrl: env.fboAppUrl })

  const ats = placement.atsSource

  const footer = submission.succeeded ? (
    <div className="flex items-center justify-between">
      <div className="flex gap-2 items-center text-emerald-700">
        <CheckCircle2 size={15} className="shrink-0" />
        <span className="text-sm font-medium">
          {t("draftWoCreatedSuccess")}
        </span>
      </div>
      <Button onClick={onClose}>{t("close")}</Button>
    </div>
  ) : (
    <>
      {/* Submission failure */}
      {submission.result && !submission.result.success && !submission.isSubmitting && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700 leading-relaxed space-y-2">
          <div className="flex gap-2">
            <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
            <span>
              {submission.result.isConflict && !submission.result.outcome
                ? t("alreadyOnboarded")
                : submission.corrections.length > 0
                  ? t("partialSubmission")
                  : t("submissionFailedTryAgain")}
            </span>
          </div>
          {submission.corrections.length > 0 && (
            <ul className="pl-5 space-y-1 list-disc">
              {submission.corrections.map((c, i) => (
                <li key={i} className={c.patch ? "text-red-700" : "text-red-500 italic"}>
                  {c.description}
                </li>
              ))}
            </ul>
          )}
          {submission.corrections.some((c) => c.patch != null) && (
            <div className="pt-1">
              <button
                type="button"
                onClick={actions.applyCorrections}
                className="text-xs font-medium text-red-700 underline hover:text-red-900"
              >
                {t("applyAndReview")}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Missing-fields list — always rendered while there are unfilled required fields, so
          the user can jump straight to each one even when an additional reason
          (status-blocked, email-editing) is also blocking submission. The reason text below
          still appears for those non-missing-field cases. */}
      {validation.missingFields.length > 0 && !submission.isSubmitting && (
        <div className="mb-2 text-right">
          <p className="text-xs text-gray-500 mb-1">{t("missingRequiredFields")}</p>
          <ul className="flex flex-wrap gap-x-2 gap-y-1 justify-end">
              {validation.missingFields.map((field) => (
                <li key={field.key}>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById(field.anchorId)
                      if (!el) return
                      el.scrollIntoView({ behavior: "smooth", block: "center" })
                      // Visible flash via Web Animations API. Native focus rings vary by
                      // input type (button vs input vs custom popover trigger) and are easy
                      // to miss; an explicit box-shadow halo on the wrapper highlights the
                      // target reliably. Using `animate` (not classList) so back-to-back
                      // clicks restart cleanly without leftover classes.
                      el.animate(
                        [
                          { boxShadow: "0 0 0 4px rgba(248, 113, 113, 0.7)", borderRadius: "8px" },
                          { boxShadow: "0 0 0 4px rgba(248, 113, 113, 0.7)", borderRadius: "8px", offset: 0.4 },
                          { boxShadow: "0 0 0 4px rgba(248, 113, 113, 0)", borderRadius: "8px" },
                        ],
                        { duration: 1500, easing: "ease-out" },
                      )
                      // Best-effort focus on the first focusable child so keyboard users land
                      // in the input. Silent no-op when nothing focusable exists.
                      const focusable = el.querySelector<HTMLElement>(
                        'input, button, [role="combobox"], [tabindex]:not([tabindex="-1"])',
                      )
                      focusable?.focus()
                    }}
                    className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100 transition-colors"
                  >
                    {field.label}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
      {/* Non-missing-field disabled reasons (status-blocked, email-editing) get a plain text
          line. Skip the line when the only reason is "Complete all required fields", since
          the missing-fields list above already conveys that. */}
      {validation.disabledReason
        && !submission.isSubmitting
        && (validation.isBlocked || validation.isEmailEditing) && (
        <p className="text-xs text-gray-400 mb-2 text-right">{validation.disabledReason}</p>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          className="bg-transparent"
        >
          {t("cancel")}
        </Button>
        <div className="relative group">
          <Button
            onClick={actions.submit}
            disabled={!validation.canSubmit || submission.isSubmitting || validation.isBlocked || validation.isEmailEditing}
            className="bg-orange-600 hover:bg-orange-700 text-white min-w-[160px]"
          >
            {submission.isSubmitting ? (
              <span className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                {t("starting")}
              </span>
            ) : (
              t("proceed")
            )}
          </Button>
          {validation.isBlocked && !submission.isSubmitting && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {worker.isStatusBlocked
                ? t("blockedPendingChanges")
                : t("clientOrgMustBeCreatedFirst")}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return (
    <DrawerShell title={title} sourceName={sourceName} idLine={placement.atsId} onClose={onClose} footer={footer}>
          {/* Body */}
          <fieldset disabled={submission.isFormLocked} className="contents">
            <div className="px-6 py-5">
              {/* Client Org missing message (Section 6) */}
              {validation.showClientOrgBanner && (
                <div className="mb-5 rounded-lg bg-red-50 border border-red-200 p-4 flex gap-3">
                  <AlertTriangle
                    size={18}
                    className="text-red-500 shrink-0 mt-0.5"
                  />
                  <p className="text-sm text-red-700 leading-relaxed">
                    {t("clientOrgMustBeCreated")}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {/* -------- Section: Mapping -------- */}
                <MappingSection
                  form={form}
                  errors={validation.errors}
                  showError={validation.showError}
                  atsSource={ats}
                  internalCompanySearch={search.internalCompany}
                  clientOrgSearch={search.clientOrg}
                  changedFields={{
                    internalCompanyChanged: diffs.internalCompanyChanged,
                    clientOrgChanged: diffs.clientOrgChanged,
                    lobChanged: diffs.lobChanged,
                  }}
                  lobs={lobs}
                  onLobCodeChange={actions.setLobCode}
                  onInternalCompanyChange={actions.setInternalCompanyId}
                  onClientOrgChange={actions.setClientOrgId}
                />

                <div className="border-t border-gray-100 mb-3" />

                {/* -------- Section: Worker Setup -------- */}
                <WorkerSetupSection
                  form={form}
                  errors={validation.errors}
                  showError={validation.showError}
                  atsSource={ats}
                  profileTypes={profileTypes}
                  contactOptions={worker.contactOptions}
                  selectedContactId={worker.selectedContactId}
                  hasNameMismatch={worker.hasNameMismatch}
                  isStatusBlocked={worker.isStatusBlocked}
                  blockedProfileUrl={worker.blockedProfileUrl}
                  workerLoading={worker.loading}
                  workerOrgSearch={search.workerOrg}
                  workerOrgRefreshKey={workerOrg.refreshKey}
                  isWorkerOrgRequired={workerOrg.isRequired}
                  isWorkerOrgVisible={workerOrg.isVisible}
                  isWorkerOrgLocked={workerOrg.isLocked}
                  selectedWorkerHasOrg={workerOrg.selectedProfileHasOrg}
                  workerOrgInputMode={workerOrg.inputMode}
                  workerOrgCanChooseUnknownMode={workerOrg.canChooseUnknownMode}
                  workerOrgExcludedIds={workerOrg.excludedOrgIds}
                  workerOrgPlaceholderName={workerOrg.placeholderName}
                  onUnlockWorkerOrg={actions.unlockWorkerOrg}
                  changedFields={{
                    profileTypeChanged: diffs.profileTypeChanged,
                    workerChanged: diffs.workerChanged,
                    workerOrgChanged: diffs.workerOrgChanged,
                    emailChanged: diffs.emailChanged,
                  }}
                  workerScenario={form.workerScenario}
                  showCreateContactAction={worker.showCreateContactAction}
                  hasExistingProfileOfSelectedType={worker.hasExistingProfileOfSelectedType}
                  onSelectContact={actions.selectContact}
                  onCreateContact={actions.createContact}
                  onUndoWorkerCreation={actions.undoWorkerCreation}
                  onEmailConfirm={actions.setWorkerEmail}
                  onEmailEditingChange={actions.setEmailEditing}
                  onProfileTypeChange={actions.setProfileTypeCode}
                  onSelectWorkerOrg={actions.selectWorkerOrg}
                  onUseExistingProfileForOrg={actions.useExistingProfileForOrg}
                  onCreateWorkerOrg={actions.createWorkerOrg}
                  onEnterWorkerOrgSearchMode={actions.enterWorkerOrgSearchMode}
                  onEnterWorkerOrgUnknownMode={actions.enterWorkerOrgUnknownMode}
                  onClearWorkerOrg={actions.clearWorkerOrg}
                  sourceLabel={sourceLabel}
                  workerPreferredCultureCode={form.workerPreferredCultureCode}
                  onWorkerPreferredCultureCodeChange={actions.setWorkerPreferredCultureCode}
                  workerAllProfiles={worker.allProfiles}
                  fboAppUrl={env.fboAppUrl}
                />

                <div className="border-t border-gray-100 mb-3" />

                {/* -------- Section: Dates & Rates -------- */}
                <DatesRatesSection
                  form={form}
                  errors={validation.errors}
                  showError={validation.showError}
                  atsSource={ats}
                  currencyItems={currencies}
                  diffs={{
                    startDateChanged: diffs.startDateChanged,
                    endDateChanged: diffs.endDateChanged,
                    billRateChanged: diffs.billRateChanged,
                    payRateChanged: diffs.payRateChanged,
                    billCurrencyChanged: diffs.billCurrencyChanged,
                    payCurrencyChanged: diffs.payCurrencyChanged,
                  }}
                  onStartDateChange={actions.setStartDate}
                  onEndDateChange={actions.setEndDate}
                  onBillRateChange={actions.setBillRate}
                  onPayRateChange={actions.setPayRate}
                  onBillCurrencyChange={actions.setBillCurrencyCode}
                  onPayCurrencyChange={actions.setPayCurrencyCode}
                  payRateCurrencyWarning={validation.payRateCurrencyWarning}
                  disabled={submission.isFormLocked}
                />

                <div className="border-t border-gray-100 mb-3" />

                {/* -------- Section: Template -------- */}
                <TemplateSection
                  value={form.templateV2Id}
                  options={templates.list}
                  disabled={submission.isFormLocked || !templates.canSelect}
                  placeholder={templates.placeholder}
                  onChange={actions.setTemplateV2Id}
                />

                {/* Divider */}
                <div className="border-t border-gray-100 mb-3" />

                {/* -------- Section D: What happens next -------- */}
                <section>
                  <div className="rounded-xl bg-blue-50/70 border border-blue-100 p-4">
                    <div className="flex gap-3">
                      <Info size={16} className="text-blue-500 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">
                          {t("whatHappensNext")}
                        </p>
                        <p className="text-xs text-blue-600 mt-1 leading-relaxed">
                          {t("whatHappensNextBody", { recordNoun, sourceName })}
                        </p>
                        <ul className="mt-3 flex flex-col gap-1.5">
                          <li className="flex items-center gap-2 text-xs text-blue-700">
                            <CheckCircle2 size={12} className="text-blue-500 shrink-0" />
                            {t("draftWorkOrderCreated")}
                          </li>
                          <li className="flex items-center gap-2 text-xs text-blue-700">
                            <CheckCircle2 size={12} className="text-blue-500 shrink-0" />
                            {t("recordRemovedFromSource", { recordNoun: recordNoun.charAt(0).toUpperCase() + recordNoun.slice(1), sourceName })}
                          </li>
                          <li className="flex items-center gap-2 text-xs text-blue-700">
                            <ArrowRight size={12} className="text-blue-500 shrink-0" />
                            {t("appearsInOnboardingTab")}
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Conditional creation summary */}
                    {creationItems.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-blue-200/60">
                        <p className="text-xs font-medium text-blue-800">
                          {t("thisActionWillCreate")}
                        </p>
                        <ul className="mt-2 flex flex-col gap-1.5">
                          {creationItems.map((item) => (
                            <li key={item} className="flex items-center gap-2 text-xs text-blue-700">
                              <Plus size={12} className="text-blue-500 shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </fieldset>
    </DrawerShell>
  )
}
